import React, { useState, useEffect } from "react";
import place3 from "../../assets/img/places/place3.jpg";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Parallax } from "react-scroll-parallax";
import { getAnnouncements, getEvents, getProjects, getStats } from "api";
import { Announcement, Event, Project } from "types";
import {
  FiUsers,
  FiVolume2,
  FiCalendar,
  FiBriefcase,
  FiHeart,
  FiShare2,
  FiAward,
  FiArrowRight,
} from "react-icons/fi";
import AboutSection from "./home/AboutSection";
import CTASection from "./home/CTASection";
import ContactSection from "./home/ContactSection";

const Home = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    getAnnouncements().then((res) =>
      setAnnouncements(
        Array.isArray(res.data.data) ? res.data.data.slice(0, 3) : []
      )
    );
    getEvents().then((res) =>
      setEvents(Array.isArray(res.data?.data) ? res.data.data.slice(0, 3) : [])
    );
    getProjects().then((res) =>
      setProjects(Array.isArray(res.data.data) ? res.data.data.slice(0, 4) : [])
    );
    getStats().then((res) => setStats(res.data));
  }, []);

  const sectionVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.42, 0, 0.58, 1] as any,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const StatCard = ({
    icon,
    value,
    label,
  }: {
    icon: React.ReactNode;
    value: number;
    label: string;
  }) => (
    <motion.div
      variants={itemVariants}
      className="rounded-lg bg-white/80 p-8 text-center shadow-sm backdrop-blur-sm"
    >
      <div className="mx-auto mb-4 text-5xl text-nature-green">{icon}</div>
      <h3 className="text-5xl font-semibold text-nature-green-dark">
        {value}+
      </h3>
      <p className="mt-2 text-lg text-gray-500">{label}</p>
    </motion.div>
  );

  return (
    <div className="bg-complementary-light text-gray-800">
      {/* Hero Section */}
      <Parallax speed={-15}>
        <div
          className="relative h-[80vh] bg-cover bg-fixed bg-center"
          style={{
            backgroundImage: `url(${place3})`,
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center bg-nature-green-dark/60 p-4 text-center text-white">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <h1 className="mb-4 text-5xl font-bold md:text-7xl">
                Welcome to Nunya
              </h1>
              <p className="mx-auto mb-8 max-w-3xl text-xl font-light md:text-2xl">
                Nunya, Isuikwuato LGA, Abia State, Nigeria
                <br />
                Fostering connection and collaboration for a brighter future.
              </p>
              <Link
                to="/about"
                className="transform rounded-full bg-white px-8 py-3 text-lg font-bold text-nature-green-dark shadow-lg transition duration-300 hover:scale-105 hover:bg-gray-200 hover:shadow-xl"
              >
                Learn More
              </Link>
            </motion.div>
          </div>
        </div>
      </Parallax>

      {/* Statistics Section */}
      {stats && (
        <motion.section
          className="bg-white py-24"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              variants={sectionVariants}
              className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4"
            >
              <StatCard
                icon={<FiUsers />}
                value={stats.total_users}
                label="Members"
              />
              <StatCard
                icon={<FiVolume2 />}
                value={stats.total_announcements}
                label="Announcements"
              />
              <StatCard
                icon={<FiCalendar />}
                value={stats.total_events}
                label="Events"
              />
              <StatCard
                icon={<FiBriefcase />}
                value={stats.total_projects}
                label="Projects"
              />
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* About Section */}
      <AboutSection />

      {/* What We Offer */}
      <motion.section
        className="bg-white py-24"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-16 text-center text-4xl font-bold text-nature-green-dark">
            Our Core Pillars
          </h2>
          <motion.div
            variants={sectionVariants}
            className="grid grid-cols-1 gap-10 text-center md:grid-cols-3"
          >
            <motion.div variants={itemVariants} className="p-8">
              <FiHeart className="mx-auto mb-4 text-5xl text-nature-green" />
              <h3 className="mb-3 text-2xl font-bold">Nunya</h3>
              <p className="text-gray-600">
                A welcoming home for support, connection, and tradition in
                Isuikwuato LGA, Abia State.
              </p>
            </motion.div>
            <motion.div variants={itemVariants} className="p-8">
              <FiShare2 className="mx-auto mb-4 text-5xl text-nature-green" />
              <h3 className="mb-3 text-2xl font-bold">Collaboration</h3>
              <p>Working together to bring innovative ideas to life.</p>
            </motion.div>
            <motion.div variants={itemVariants} className="p-8">
              <FiAward className="mx-auto mb-4 text-5xl text-nature-green" />
              <h3 className="mb-3 text-2xl font-bold">Growth</h3>
              <p>Opportunities for personal and professional development.</p>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Announcements & Events */}
      <motion.section
        className="bg-complementary-light py-24"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
            <motion.div variants={itemVariants}>
              <h2 className="mb-8 flex items-center text-3xl font-bold text-nature-green-dark">
                <FiVolume2 className="mr-4" />
                Latest News
              </h2>
              <div className="space-y-6">
                {announcements.map((ann) => (
                  <div
                    key={ann.id}
                    className="rounded-lg bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <h3 className="text-xl font-bold text-gray-800">
                      {ann.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Posted by {ann.creator?.firstname} on{" "}
                      {new Date(ann.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div variants={itemVariants}>
              <h2 className="mb-8 flex items-center text-3xl font-bold text-nature-green-dark">
                <FiCalendar className="mr-4" />
                Upcoming Events
              </h2>
              <div className="space-y-6">
                {events.map((evt) => (
                  <div
                    key={evt.id}
                    className="rounded-lg bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <h3 className="text-xl font-bold text-gray-800">
                      {evt.name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {new Date(evt.event_date).toLocaleString([], {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Featured Projects */}
      <motion.section
        className="bg-white py-24"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-16 text-center text-4xl font-bold text-nature-green-dark">
            Featured Initiatives
          </h2>
          <motion.div
            variants={sectionVariants}
            className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4"
          >
            {projects.map((project) => (
              <Link to={`/projects/${project.id}`} key={project.id}>
                <motion.div
                  variants={itemVariants}
                  className="group transform overflow-hidden rounded-lg bg-white shadow-md transition-transform duration-300 hover:-translate-y-2"
                >
                  <div className="relative">
                    <img
                      src={
                        project.images?.length > 0
                          ? project.images?.[0]?.image_path
                          : "https://source.unsplash.com/random/400x300?nature,architecture"
                      }
                      alt={project.title}
                      className="h-56 w-full object-cover"
                    />
                    <div className="bg-black/10 group-hover:bg-black/30 absolute inset-0 transition-colors duration-300"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="mb-2 text-xl font-bold text-nature-green-dark">
                      {project.title ?? "Untitled Project"}
                    </h3>
                    <p className="line-clamp-3 text-gray-600">
                      {project.description ?? "No description available."}
                    </p>
                  </div>
                </motion.div>
              </Link>
            ))}
          </motion.div>
          <div className="mt-16 text-center">
            <Link
              to="/projects"
              className="rounded-full bg-nature-green px-8 py-3 text-lg font-bold text-white shadow-md transition duration-300 hover:bg-nature-green-dark hover:shadow-lg"
            >
              View All Initiatives
            </Link>
          </div>
        </div>
      </motion.section>

      <CTASection />

      <ContactSection />
    </div>
  );
};

export default Home;
